import React, { useState } from 'react'
import classNames from 'classnames'
import { Badge } from '@toasttab/shared/src/components'
import Button from '@toasttab/shared/src/components/atoms/Button/Button'
import TextLink from '@toasttab/shared/src/components/molecules/TextLink/TextLink'
import { useI18next } from '@toasttab/shared/src/hooks/useI18next'

const BusinessTypes = ({ body }: { body: any }) => {
  const { t } = useI18next()
  const [businessType, setBusinessType] = useState<'Restaurants' | 'Retailers'>(
    body?.tabs?.[0]?.title as 'Restaurants' | 'Retailers'
  )
  const [typeCategory, setTypeCategory] = useState<string | null>(
    body?.tabs?.find((item: any) => item.title === businessType)?.categroies[0]
      ?.title
  )

  const businessTypeStyles = (selected: boolean) =>
    classNames('!w-1/2 !h-11 !px-6 !py-2.5', {
      '!bg-black/100 !text-white': selected,
      '!bg-transparent !text-default': !selected
    })

  const categorySectionStyles = (selected: boolean) =>
    classNames({
      block: selected,
      hidden: !selected
    })
  const typeCategoryStyles = (selected: boolean) =>
    classNames('mb-6 last:mb-0 px-8 hidden lg:block', {
      'border-l-2 border-brand-50': selected,
      'border-none': !selected
    })

  const categoryLinkStyles = (selected: boolean) =>
    classNames('cursor-pointer mt-1', {
      block: selected,
      hidden: !selected
    })

  const switchCategory = (categoryOption: string) => {
    for (const category of body.tabs) {
      const selectedCategory = category.categroies.find(
        (category: any) => category.title === categoryOption
      )
      if (selectedCategory) {
        setTypeCategory(selectedCategory.title)
        break
      }
    }
  }
  const updateBusinessType = (type: 'Restaurants' | 'Retailers') => {
    setBusinessType(type)

    const defaultCategory = body.tabs.find((item: any) => item.title === type)

    if (defaultCategory !== null) {
      const firstCategory = defaultCategory?.categroies?.[0]?.title
      setTypeCategory(firstCategory as string)
    }
  }

  const renderAsset = (src: string, title: string, sizes: number[]) => {
    const assetType = src.split('.').pop()

    if (assetType === 'mp4') {
      return (
        <video
          autoPlay
          className='w-auto'
          loop
          muted
          playsInline
          preload='metadata'
        >
          <source src={src} type='video/mp4' />
          Your browser does not support the video tag.
        </video>
      )
    } else {
      return (
        <picture className='w-full h-full'>
          <source
            sizes={`${sizes[1]}px`}
            type='image/avif'
            srcSet={
              src +
              '?fm=avif&w=' +
              sizes[1] +
              '&fit=scale ' +
              sizes[1] +
              'w, ' +
              src +
              '?fm=avif&w=' +
              sizes[1] * 2 +
              '&fit=scale ' +
              sizes[1] * 2 +
              'w'
            }
          />
          <source
            sizes={`${sizes[0]}px`}
            type='image/webp'
            srcSet={
              src +
              '?fm=webp&w=' +
              sizes[0] +
              '&fit=scale ' +
              sizes[0] +
              'w, ' +
              src +
              '?fm=webp&w=' +
              sizes[0] * 2 +
              '&fit=scale ' +
              sizes[0] * 2 +
              'w'
            }
          />
          <source srcSet={src + '?fm=avif'} type='image/avif' />
          <img
            alt={title}
            className='w-full h-full object-cover object-center rounded-base'
            loading='lazy'
            srcSet={src}
          />
        </picture>
      )
    }
  }

  const renderDesktopAssets = (assets: any) => {
    return (
      <div className='grid grid-cols-[36%_1fr] gap-2 md:gap-6 w-full'>
        <div className='flex flex-col gap-2 md:gap-6'>
          {renderAsset(
            assets[0]?.src,
            assets[0]?.title ?? 'asset 1',
            [130, 264]
          )}
          {renderAsset(
            assets[1]?.src,
            assets[1]?.title ?? 'asset 2',
            [130, 264]
          )}
        </div>
        {renderAsset(assets[2]?.src, assets[2]?.title ?? 'asset 3', [223, 456])}
      </div>
    )
  }

  return (
    <section className='mb-10 lg:mb-16 overflow-hidden'>
      <div className='ds-wrapper'>
        <div className='ds-container'>
          <div className='flex flex-col lg:gap-x-6 lg:grid lg:grid-cols-[360px_3fr] lg:grid-rows-[minmax(auto,_auto)_1fr] lg:min-h-[564px] bg-gradient-to-t from-[#F6F1EE] via-[#F6F1EE] to-[#F9F9F9] rounded-[2rem] lg:bg-none'>
            <div className='order-1 lg:order-none lg:col-start-1 lg:row-start-1 flex items-center justify-center'>
              <div className='bg-[#F9F9F9] rounded-t-[2rem] w-full'>
                <div className='bg-[#F6F1EE] bg-blend-multiply bg-[#F6F1EE80]/50 p-2.5 rounded-[2rem]'>
                  {body.tabs.map((type: any, index: any) => (
                    <Button
                      classes={businessTypeStyles(businessType === type.title)}
                      heapInteraction={type.heapInteraction}
                      key={index}
                      size='large'
                      variation='pill'
                      onClick={() =>
                        updateBusinessType(
                          type.title as 'Restaurants' | 'Retailers'
                        )
                      }
                    >
                      {type.title}
                      {type.badge ? (
                        <Badge
                          badgeColor='bg-yellow-25'
                          classes='ml-2.5'
                          text={type.badge}
                          uppercase={true}
                        />
                      ) : null}
                    </Button>
                  ))}
                </div>
              </div>
            </div>
            <div className='order-3 lg:order-none lg:col-start-2 lg:row-span-2'>
              {body.tabs.map((type: any, typeIndex: any) => (
                <React.Fragment key={`media-${type.title}-${typeIndex}`}>
                  {type.categroies.map((category: any, index: any) => (
                    <React.Fragment
                      key={`category-${type.title}-${category.title}-${index}`}
                    >
                      <div
                        key={index}
                        className={
                          typeCategory === (category.title as string)
                            ? 'flex px-4 lg:p-0 h-full w-full'
                            : 'hidden'
                        }
                      >
                        {renderDesktopAssets(category.assets)}
                      </div>
                      <div
                        className={
                          typeCategory === (category.title as string)
                            ? 'p-4 lg:hidden'
                            : 'hidden'
                        }
                      >
                        <Button
                          arrow={true}
                          classes='block lg:hidden'
                          fullwidth={true}
                          href={category.link ? category.link : '/'}
                          size='large'
                          variation='primary'
                        >
                          Learn More
                        </Button>
                      </div>
                    </React.Fragment>
                  ))}
                </React.Fragment>
              ))}
            </div>
            <div className='order-2 lg:order-none lg:col-start-1 lg:row-start-2 flex items-center justify-center'>
              <div className='bg-none lg:bg-gradient-to-t from-[#F6F1EE] via-[#F6F1EE] to-[#F9F9F9] rounded-b-[2rem] w-full h-full'>
                <div className='py-4 lg:py-6'>
                  {body.tabs.map((type: any, index: any) => (
                    <div
                      key={index}
                      className={categorySectionStyles(
                        businessType === type.title
                      )}
                    >
                      <div className='px-4 lg:hidden'>
                        <label
                          className='sr-only'
                          htmlFor='business-type-select'
                        >
                          {t('Business Type')}
                        </label>
                        <select
                          className='block lg:hidden text-secondary px-4 py-3 rounded-[100px] bg-white bg-arrow-drop-down-dark bg-no-repeat bg-[center_right_1rem] appearance-none w-full'
                          id='business-type-select'
                          onChange={(e) => switchCategory(e.target.value)}
                          value={typeCategory}
                        >
                          {type.categroies.map((category: any, index: any) => (
                            <option
                              data-link={category.link}
                              key={index}
                              value={category.title}
                            >
                              {category.title}
                            </option>
                          ))}
                        </select>
                      </div>
                      {type.categroies.map((category: any, index: any) => (
                        <React.Fragment
                          key={`${type.title}-${category.title}-${index}`}
                        >
                          <div
                            key={index}
                            className={typeCategoryStyles(
                              typeCategory === (category.title as string)
                            )}
                          >
                            <Button
                              classes='!h-auto !p-0 font-effra font-normal lg:!leading-[2rem] lg:!text-[1.625rem] !text-secondary'
                              heapInteraction={category.heapInteraction}
                              key={index}
                              onClick={() => switchCategory(category.title)}
                              size='large'
                              variation='text'
                            >
                              {category.title}
                            </Button>
                            <TextLink
                              href={category.link}
                              icon='arrow'
                              isDisplayedBlock
                              text={`${t('Learn More')}`}
                              wrapperClasses={categoryLinkStyles(
                                typeCategory === (category.title as string)
                              )}
                            />
                          </div>
                        </React.Fragment>
                      ))}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}

export default BusinessTypes
